import React, { useEffect, useState } from 'react';
import API from '../../Api';

export default function SettingsApiKeys() {
  const [keys, setKeys] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('Production API');
  const [environment, setEnvironment] = useState('production');
  const [createdKey, setCreatedKey] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await API.get('/api/api-keys');
      setKeys(res.data?.keys || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const createKey = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await API.post('/api/api-keys', { name, environment });
      setCreatedKey(res.data);
      setCreateOpen(false);
      setName('Production API');
      setEnvironment('production');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create API key');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm('Revoke this API key? Applications using it will stop authenticating.')) return;
    setError('');
    try {
      await API.delete(`/api/api-keys/${id}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to revoke API key');
    }
  };

  const formatDate = (value) => {
    if (!value) return 'Never';
    try {
      return new Date(value).toLocaleString();
    } catch {
      return String(value);
    }
  };

  if (loading) {
    return <div className="settings-panel">Loading API keys…</div>;
  }

  return (
    <div className="settings-panel">
      <h2>API Keys</h2>
      <p className="settings-subtitle">
        Credentials for sending logs from your applications into this organization&apos;s workspace.
      </p>
      {error && <div className="settings-alert settings-alert--error">{error}</div>}

      <div className="settings-actions" style={{ marginBottom: 12 }}>
        <button type="button" className="settings-btn settings-btn--primary" onClick={() => setCreateOpen(true)}>
          + Create API Key
        </button>
      </div>

      {keys.length === 0 ? (
        <p style={{ color: '#6b7280' }}>No active API keys yet.</p>
      ) : (
        keys.map((key) => (
          <div key={key.id} className="settings-key-row">
            <div>
              <strong>{key.name}</strong>
              <div className="settings-mono" style={{ margin: '8px 0', fontSize: 13 }}>
                {key.maskedKey || `${key.keyPrefix}••••••••••••••••`}
              </div>
              <div style={{ color: '#6b7280', fontSize: 13 }}>
                {(key.environment || 'production').toUpperCase()} · Created {formatDate(key.createdAt)} · Last used{' '}
                {formatDate(key.lastUsedAt)}
              </div>
            </div>
            <button type="button" className="settings-btn settings-btn--danger" onClick={() => revoke(key.id)}>
              Revoke
            </button>
          </div>
        ))
      )}

      {createOpen && (
        <div className="settings-modal-backdrop" onClick={() => setCreateOpen(false)}>
          <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginTop: 0, fontSize: 22 }}>Create API Key</h2>
            <div className="settings-field">
              <label>Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Production Server" />
            </div>
            <div className="settings-field">
              <label>Environment</label>
              <select value={environment} onChange={(e) => setEnvironment(e.target.value)}>
                <option value="production">Production</option>
                <option value="staging">Staging</option>
                <option value="development">Development</option>
              </select>
            </div>
            <div className="settings-actions">
              <button type="button" className="settings-btn settings-btn--primary" onClick={createKey} disabled={busy}>
                Create
              </button>
              <button type="button" className="settings-btn settings-btn--secondary" onClick={() => setCreateOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {createdKey?.rawKey && (
        <div className="settings-modal-backdrop" onClick={() => setCreatedKey(null)}>
          <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginTop: 0, fontSize: 22 }}>Your API key</h2>
            <p>Copy it now. You won&apos;t be able to see it again.</p>
            <div className="settings-mono">{createdKey.rawKey}</div>
            <div className="settings-actions">
              <button
                type="button"
                className="settings-btn settings-btn--primary"
                onClick={() => navigator.clipboard?.writeText(createdKey.rawKey)}
              >
                Copy
              </button>
              <button type="button" className="settings-btn settings-btn--secondary" onClick={() => setCreatedKey(null)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
